import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient,
} from "react-query";
import { useTranslation } from "react-i18next";
import { App } from "antd";
import _ from "lodash";

import * as api from "apis/taskActivityEss";

export const useTaskActivitiesEss = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["taskActivities/ess", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskActivitiesEss(payload);
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export const useTaskActivitiesPaginationEss = (payload = {}, options = {}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useInfiniteQuery(
    ["taskActivities/pagination/ess", payload],
    async ({ pageParam = 1 }) => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskActivitiesEss({ page: pageParam, ...payload });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined;
        }
        return lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined;
      },
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
};

export function useTaskActivityEss(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["task/ess", payload],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await api.getTaskActivityEss(payload?.id);
        response = data;
      } catch (error) {}
      return response;
    },
    {
      onError: (error) =>
        message.error(t("error.failed_to_load_data_from_server")),
      ...options,
    }
  );
}

export const useAddTaskActivityEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => api.addTaskActivityEss(payload), {
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useEditTaskActivityEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();

  return useMutation(
    (payload) => {
      const editPayload = _.cloneDeep(payload);
      delete editPayload.payloadList;
      return api.editTaskActivityEss(editPayload);
    },
    {
      onMutate: async (payload) => {
        await queryClient.cancelQueries([
          "taskActivities/pagination/ess",
          payload.payloadList,
        ]);

        const prev = queryClient.getQueryData([
          "taskActivities/pagination/ess",
          payload.payloadList,
        ]);

        queryClient.setQueryData(
          ["taskActivities/pagination/ess", payload.payloadList],
          (old) => {
            if (old) {
              const pages = _.cloneDeep(old?.pages);
              const newPages = pages.map((page) => {
                const updateData = page.data.map((row) => {
                  if (row?.id === payload?.id) {
                    row.content = payload.content;
                  }
                  return row;
                });
                return { ...page, data: updateData };
              });
              old.pages = newPages;
              return old;
            }
          }
        );
        return { prev };
      },
      onSuccess: ({ data }) => {
        if (data?.success) {
          message.success(data?.message);
        } else {
          message.error(data?.message);
        }
      },
      onError: (error, payload, context) => {
        queryClient.setQueryData(
          ["taskActivities/pagination/ess", payload.payloadList],
          context.prev
        );
        message.error(t("error.failed_save_data_to_server"));
      },
      onSettled: (data, error, payload) => {
        queryClient.invalidateQueries([
          "taskActivities/pagination/ess",
          payload.payloadList,
        ]);
      },
    }
  );
};

export const useDeleteTaskActivityEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();

  return useMutation((payload) => api.deleteTaskActivityEss(payload?.id), {
    onMutate: async (payload) => {
      await queryClient.cancelMutations([
        "taskActivities/pagination/ess",
        payload.payloadList,
      ]);

      const prev = queryClient.getQueryData([
        "taskActivities/pagination/ess",
        payload.payloadList,
      ]);

      queryClient.setQueryData(
        ["taskActivities/pagination/ess", payload.payloadList],
        (old) => {
          if (old) {
            const pages = _.cloneDeep(old.pages);
            const newPages = pages.map((page) => {
              const filterPage = page.data.filter(
                (row) => row?.id !== payload?.id
              );
              return { ...page, data: filterPage };
            });
            old.pages = newPages;
            return old;
          }
        }
      );
      return { prev };
    },
    onSuccess: ({ data }) => {
      if (data?.success) {
        message.success(data?.message);
      } else {
        message.error(data?.message);
      }
    },
    onError: (error, payload, context) => {
      queryClient.setQueryData(
        ["taskActivities/pagination/ess", payload.payloadList],
        context.prev
      );
      message.error(t("error.failed_save_data_to_server"));
    },
    onSettled: (data, error, payload) => {
      queryClient.invalidateQueries([
        "taskActivities/pagination/ess",
        payload.payloadList,
      ]);
    },
  });
};

export const useDeleteAttachmentTaskActivityEss = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation(
    (payload) => api.deleteAttachmentTaskActivityEss(payload),
    {
      onSuccess: ({ data }) => {
        if (data?.success) {
          message.success(data?.message);
        } else {
          message.error(data?.message);
        }
      },
      onError: (error) => message.error(t("error.failed_save_data_to_server")),
    }
  );
};
